import { Pressable, StyleSheet, Text } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Radius, Spacing } from '@/constants/theme';
import { useFilters } from '@/hooks/use-filters';
import { useTheme } from '@/hooks/use-theme';
import { categories } from '@/lib/events-data';

import css from './category-nav.module.css';

// Web-only: column count used to be a JS `isLg`/`isMd` check plus a measured
// `itemWidth` (onLayout), so it rendered 5 columns, then remeasured to 6 or
// 10 after mount — a visible height jump on top of the earlier width flash.
// CSS grid (category-nav.module.css) picks the column count with the same
// breakpoints, resolved by the browser at first paint.
export function CategoryNav() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { activeCategory, setActiveCategory } = useFilters();

  return (
    <div className={css.section}>
      <div className={css.grid}>
        {categories.map((cat) => {
          const active = activeCategory === cat.id;
          const name = t(`categories.${cat.id}`);
          return (
            <Pressable
              key={cat.id}
              accessibilityRole="button"
              accessibilityLabel={name}
              accessibilityState={{ selected: active }}
              onPress={() => setActiveCategory(active ? 'all' : cat.id)}
              style={({ pressed }) => [
                styles.item,
                {
                  backgroundColor: active ? theme.accent : theme.card,
                  borderColor: active ? theme.gold : theme.border,
                },
                pressed && { borderColor: theme.gold, opacity: 0.95 },
              ]}>
              <cat.icon size={22} color={active ? theme.gold : theme.primary} />
              <Text
                style={[
                  styles.label,
                  { color: active ? theme.accentForeground : theme.text },
                ]}
                numberOfLines={2}>
                {name}
              </Text>
            </Pressable>
          );
        })}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  item: {
    borderWidth: 1,
    borderRadius: Radius.xl,
    paddingVertical: Spacing.two + 2,
    paddingHorizontal: Spacing.one,
    alignItems: 'center',
    gap: 6,
  },
  label: {
    fontSize: 11,
    fontWeight: '500',
    textAlign: 'center',
  },
});
